import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SearchableFacilitySelect from '../components/SearchableFacilitySelect';
import { facilityService, procedureService, requestService, shaService, vendorService } from '../services/dataService';
import ReceivedStudiesTable from '../components/ReceivedStudiesTable';
import WorklistRepublishPanel from '../components/WorklistRepublishPanel';
import { useAuth } from '../hooks/useAuth';
import { hasPermission } from '../utils/permissions';
import styles from './assets/css/RequestsPage.module.css';

const requestStatuses = ['pending', 'in_progress', 'completed', 'failed', 'cancelled'];

const initialCreateForm = {
  request_id: '',
  patient_id: '',
  patient_first_name: '',
  date_of_birth: '',
  sex: 'U',
  modality: '',
  description: '',
  institution_name: '',
  facility_id: '',
  callback_url: '',
  callback_auth_type: 'none',
  callback_auth_token: '',
  idempotency_key: '',
  claim_id: '',
  payor: '',
  preauth_code: '',
  procedures: [],
  request_metadata: '',
};

const toLabel = (value) => (value || '').replace(/_/g, ' ').toUpperCase();

const toDateTime = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '-';
  return dt.toLocaleString();
};

const getErrorMessage = (err, fallback) => err?.response?.data?.detail || err?.message || fallback;

const buildTimeline = (detail, callbackLogs) => {
  if (!detail) return [];
  const events = [];

  events.push({
    key: `created-${detail.id}`,
    at: detail.created_at,
    title: 'Request accepted',
    description: `EMR ID ${detail.emr_request_id}`,
  });

  if (detail.status === 'in_progress' || detail.status === 'failed' || detail.status === 'cancelled') {
    events.push({
      key: `status-${detail.id}`,
      at: detail.updated_at,
      title: `Status changed to ${toLabel(detail.status)}`,
      description: detail.status_message || 'Processing state updated',
    });
  }

  if (detail.completed_at) {
    events.push({
      key: `completed-${detail.id}`,
      at: detail.completed_at,
      title: 'Request completed',
      description: 'Result callback flow completed',
    });
  }

  (detail.equipment_assignments || []).forEach((assignment) => {
    events.push({
      key: `assignment-${assignment.id}`,
      at: assignment.created_at,
      title: 'Equipment assigned',
      description: `Equipment ${assignment.equipment_id} (${assignment.assignment_status})`,
    });
    if (assignment.mwl_sent_at) {
      events.push({
        key: `mwl-${assignment.id}`,
        at: assignment.mwl_sent_at,
        title: 'MWL dispatched',
        description: `Message ${assignment.mwl_message_id || 'generated'}`,
      });
    }
    if (assignment.result_received_at) {
      events.push({
        key: `result-${assignment.id}`,
        at: assignment.result_received_at,
        title: 'Equipment result received',
        description: `MPPS message ${assignment.mpps_message_id || 'received'}`,
      });
    }
  });

  (callbackLogs || []).forEach((log) => {
    events.push({
      key: `callback-start-${log.id}`,
      at: log.created_at,
      title: `Callback attempt #${log.attempt_number}`,
      description: `${toLabel(log.callback_status)}${log.http_status_code ? ` (HTTP ${log.http_status_code})` : ''}`,
    });
    if (log.completed_at) {
      events.push({
        key: `callback-end-${log.id}`,
        at: log.completed_at,
        title: `Callback attempt #${log.attempt_number} finished`,
        description: log.error_message || 'Callback completed',
      });
    }
  });

  const auditEvents = detail.request_metadata?.audit_events;
  if (Array.isArray(auditEvents)) {
    auditEvents.forEach((event, idx) => {
      const eventType = (event?.type || '').toString();
      const eventTitle = eventType === 'request_retargeted'
        ? 'Request retargeted manually'
        : eventType === 'worklist_resent' || eventType === 'mwl_regenerated'
          ? 'Worklist resent manually'
          : eventType === 'worklist_pulled'
            ? 'Worklist pulled by machine'
            : 'Manual intervention';
      const eventDetails = event?.details || {};
      const fallbackDescription = event?.message || 'Manual request intervention recorded';
      const description = eventType === 'request_retargeted'
        ? `Facility ${eventDetails.previous_facility_id || '-'} to ${eventDetails.new_facility_id || '-'}, equipment ${eventDetails.previous_equipment_id || '-'} to ${eventDetails.new_equipment_id || '-'}`
        : eventType === 'worklist_resent' || eventType === 'mwl_regenerated'
          ? `Published ${eventDetails.published_worklists || eventDetails.generated_files || 0} worklist(s), verified ${eventDetails.succeeded || 0}/${eventDetails.queued || 0}`
          : fallbackDescription;

      events.push({
        key: `audit-${detail.id}-${idx}-${eventType}`,
        at: event?.at,
        title: eventTitle,
        description,
      });
    });
  }

  return events
    .filter((event) => event.at)
    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
};

const sharePercent = (part, whole) => `${whole ? Math.round((part / whole) * 100) : 0}%`;

const RequestsPage = () => {
  const { user } = useAuth();
  const canWriteRequests = hasPermission(user, 'requests', 'write');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [exporting, setExporting] = useState(false);

  const [statusFilter, setStatusFilter] = useState('');
  const [patientFilter, setPatientFilter] = useState('');
  const [facilityFilter, setFacilityFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [claimFilter, setClaimFilter] = useState('');
  const [preauthFilter, setPreauthFilter] = useState('');
  const [claimSummary, setClaimSummary] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState('');

  const [selectedRequestId, setSelectedRequestId] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [callbackLogs, setCallbackLogs] = useState([]);
  const [retargetFacilityId, setRetargetFacilityId] = useState('');
  const [retargetEquipmentId, setRetargetEquipmentId] = useState('');
  const [retargetEquipmentOptions, setRetargetEquipmentOptions] = useState([]);
  const [retargetLoading, setRetargetLoading] = useState(false);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [simulateResultLoading, setSimulateResultLoading] = useState(false);
  const [simulateProcedureCode, setSimulateProcedureCode] = useState('');
  const [simulateEquipmentId, setSimulateEquipmentId] = useState('');
  const [requestActionMessage, setRequestActionMessage] = useState('');
  const [claimForm, setClaimForm] = useState({ claim_id: '', payor: '', preauth_code: '' });
  const [claimUpdateLoading, setClaimUpdateLoading] = useState(false);
  const [shaVerificationSource, setShaVerificationSource] = useState('hmis');
  const [shaVerificationLoading, setShaVerificationLoading] = useState(false);
  const [shaVerificationResult, setShaVerificationResult] = useState(null);

  const [facilities, setFacilities] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(initialCreateForm);
  const [createLoading, setCreateLoading] = useState(false);
  const [createMessage, setCreateMessage] = useState('');

  const [autoRefresh, setAutoRefresh] = useState(true);
  const [metrics, setMetrics] = useState({
    total: 0,
    completed: 0,
    failed: 0,
    cancelled: 0,
    terminal: 0,
    successRate: 0,
  });

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);
  const facilityById = useMemo(
    () => facilities.reduce((acc, facility) => {
      acc[facility.id] = facility;
      return acc;
    }, {}),
    [facilities],
  );

  const getFacilityName = useCallback((facilityId) => (
    facilityById[facilityId]?.name || 'Unknown Facility'
  ), [facilityById]);

  const getRequestFilters = useCallback(() => ({
    status: statusFilter || undefined,
    patient: patientFilter || undefined,
    facility_name: facilityFilter || undefined,
    vendor_id: vendorFilter || undefined,
    has_claim: claimFilter || undefined,
    has_preauth: preauthFilter || undefined,
  }), [statusFilter, patientFilter, facilityFilter, vendorFilter, claimFilter, preauthFilter]);

  const loadRequests = useCallback(async (targetPage = page, silent = false) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const data = await requestService.getAll({
        page: targetPage,
        page_size: pageSize,
        ...getRequestFilters(),
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setClaimSummary(data.summary || null);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load requests'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, pageSize, getRequestFilters]);

  const loadRequestDetail = useCallback(async (requestId, silent = false) => {
    if (!requestId) return;
    if (!silent) setDetailLoading(true);
    try {
      const [detail, logs] = await Promise.all([
        requestService.getById(requestId),
        requestService.getCallbackLogs(requestId),
      ]);
      setSelectedRequest(detail);
      setCallbackLogs(logs || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load request detail'));
    } finally {
      if (!silent) setDetailLoading(false);
    }
  }, []);

  const loadMetrics = useCallback(async () => {
    try {
      const data = await requestService.getSuccessMetrics();
      setMetrics(data);
    } catch (_) {
      // Keep page usable if analytics call fails.
    }
  }, []);

  const loadFormDependencies = useCallback(async () => {
    try {
      const [facilityData, procedureData, modalityData] = await Promise.all([
        facilityService.getAllPages(),
        procedureService.getAll(0, 200),
        vendorService.getModalities({ status: 'active' }),
      ]);

      const facilityItems = facilityData || [];
      const procedureItems = procedureData || [];
      const modalityItems = modalityData.items || [];
      setFacilities(facilityItems);
      setProcedures(procedureItems);
      setModalities(modalityItems);

      setCreateForm((prev) => ({
        ...prev,
        facility_id: prev.facility_id || facilityItems[0]?.id || '',
        modality: prev.modality || modalityItems[0]?.code || '',
      }));
    } catch (_) {
      // Create form can still work with manually entered fields.
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    loadMetrics();
    loadFormDependencies();
  }, [loadMetrics, loadFormDependencies]);

  useEffect(() => {
    // The vendor filter is hidden for users who may not list vendors.
    vendorService.getAllPages().then((data) => setVendors(data || [])).catch(() => setVendors([]));
  }, []);

  useEffect(() => {
    if (!selectedRequestId) return;
    loadRequestDetail(selectedRequestId);
  }, [selectedRequestId, loadRequestDetail]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const interval = window.setInterval(() => {
      loadRequests(page, true);
      loadMetrics();
      if (selectedRequestId) {
        loadRequestDetail(selectedRequestId, true);
      }
    }, 10000);
    return () => window.clearInterval(interval);
  }, [autoRefresh, page, selectedRequestId, loadRequests, loadRequestDetail, loadMetrics]);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) => [
      item.emr_request_id,
      item.internal_request_id,
      item.patient_id,
      item.patient_first_name,
      item.claim_id,
      item.payor,
      item.preauth_code,
      item.request_metadata?.date_of_birth,
      item.request_metadata?.sex,
      item.request_metadata?.modality,
      item.request_metadata?.institution_name,
      getFacilityName(item.facility_id),
      item.facility_id,
      item.status,
    ].some((value) => (value || '').toString().toLowerCase().includes(term)));
  }, [items, search, getFacilityName]);

  const sortedItems = useMemo(() => {
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    const getValue = (item) => {
      if (sortConfig.key === 'facility') return getFacilityName(item.facility_id);
      return item[sortConfig.key] || '';
    };
    return [...filteredItems].sort((a, b) => String(getValue(a)).localeCompare(String(getValue(b))) * direction);
  }, [filteredItems, sortConfig, getFacilityName]);

  const timeline = useMemo(() => buildTimeline(selectedRequest, callbackLogs), [selectedRequest, callbackLogs]);
  const currentAssignment = useMemo(() => (selectedRequest?.equipment_assignments || [])[0] || null, [selectedRequest]);
  const hasMwlGenerated = useMemo(() => {
    const worklists = selectedRequest?.request_metadata?.worklists || selectedRequest?.request_metadata?.worklist_files;
    if (Array.isArray(worklists) && worklists.length > 0) return true;
    return Boolean((selectedRequest?.equipment_assignments || []).find((item) => item.mwl_sent_at));
  }, [selectedRequest]);

  const onApplyFilters = (e) => {
    e.preventDefault();
    setPage(1);
    loadRequests(1);
  };

  const changeSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const escapeCsv = (value) => {
    const text = value == null ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const downloadCsv = (filename, rows, columns) => {
    const csv = [
      columns.map(([, label]) => escapeCsv(label)).join(','),
      ...rows.map((row) => columns.map(([key]) => escapeCsv(typeof key === 'function' ? key(row) : row[key])).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportRequests = async () => {
    setExporting(true);
    setError('');
    try {
      const allItems = [];
      let currentPage = 1;
      let totalItems = 0;
      do {
        const data = await requestService.getAll({
          page: currentPage,
          page_size: 100,
          ...getRequestFilters(),
        });
        const pageItems = data.items || [];
        totalItems = data.total || 0;
        allItems.push(...pageItems);
        currentPage += 1;
      } while (allItems.length < totalItems);

      const term = search.trim().toLowerCase();
      const exportItems = term
        ? allItems.filter((item) => [
          item.emr_request_id,
          item.internal_request_id,
          item.patient_id,
          item.patient_first_name,
          item.claim_id,
          item.payor,
          item.preauth_code,
          getFacilityName(item.facility_id),
          item.facility_id,
          item.status,
        ].some((value) => (value || '').toString().toLowerCase().includes(term)))
        : allItems;

      downloadCsv(
        `requests-${new Date().toISOString().slice(0, 10)}.csv`,
        exportItems,
        [
          [(row) => exportItems.indexOf(row) + 1, '#'],
          ['internal_request_id', 'Internal ID'],
          ['emr_request_id', 'EMR ID'],
          ['patient_id', 'Patient ID'],
          ['patient_first_name', 'Patient'],
          ['claim_id', 'Claim ID'],
          ['payor', 'Payor'],
          ['preauth_code', 'Preauth Code'],
          [(row) => getFacilityName(row.facility_id), 'Facility'],
          ['facility_id', 'FID'],
          ['status', 'Status'],
          ['created_at', 'Created'],
        ],
      );
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to export requests'));
    } finally {
      setExporting(false);
    }
  };

  const handleProcedureSelection = (code) => {
    setCreateForm((prev) => {
      const selected = new Set(prev.procedures);
      if (selected.has(code)) selected.delete(code);
      else selected.add(code);
      return { ...prev, procedures: Array.from(selected) };
    });
  };

  const generateIdempotencyKey = () => {
    const prefix = createForm.request_id.trim() || 'REQ';
    const stamp = Date.now();
    setCreateForm((prev) => ({ ...prev, idempotency_key: `${prefix}-${stamp}` }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setCreateMessage('');

    if (createForm.procedures.length === 0) {
      setError('Select at least one procedure code');
      return;
    }

    let metadataPayload = {};
    if (createForm.request_metadata.trim()) {
      try {
        metadataPayload = JSON.parse(createForm.request_metadata);
      } catch (_) {
        setError('Request metadata must be valid JSON');
        return;
      }
    }

    setCreateLoading(true);
    try {
      const payload = {
        request_id: createForm.request_id,
        patient_id: createForm.patient_id,
        patient_first_name: createForm.patient_first_name,
        date_of_birth: createForm.date_of_birth || null,
        sex: createForm.sex || 'U',
        modality: createForm.modality || null,
        description: createForm.description || null,
        institution_name: createForm.institution_name || null,
        procedures: createForm.procedures,
        facility_id: createForm.facility_id,
        callback_url: createForm.callback_url || null,
        callback_auth_type: createForm.callback_auth_type || 'none',
        callback_auth_token: createForm.callback_auth_token || null,
        idempotency_key: createForm.idempotency_key,
        claim_id: createForm.claim_id || null,
        payor: createForm.payor || null,
        preauth_code: createForm.preauth_code || null,
        request_metadata: {
          ...metadataPayload,
          date_of_birth: createForm.date_of_birth || null,
          sex: createForm.sex || 'U',
          modality: createForm.modality || null,
          description: createForm.description || null,
          institution_name: createForm.institution_name || null,
        },
      };

      const result = await requestService.intake(payload);
      const createdRequests = Array.isArray(result.created_requests) ? result.created_requests : [];
      if ((result.split_count || 1) > 1 && createdRequests.length > 0) {
        const childIds = createdRequests.map((item) => item.internal_request_id).join(', ');
        setCreateMessage(
          `Request split into ${result.split_count} internal requests (${childIds}).`
        );
      } else {
        setCreateMessage(`Request accepted: ${result.internal_request_id} (${toLabel(result.status)})`);
      }
      setCreateForm((prev) => ({
        ...initialCreateForm,
        facility_id: prev.facility_id,
        modality: prev.modality,
      }));
      setShowCreateModal(false);
      await loadRequests(1);
      setPage(1);
      await loadMetrics();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create request'));
    } finally {
      setCreateLoading(false);
    }
  };

  const openRequestDetail = (requestId) => {
    setError('');
    setRequestActionMessage('');
    setSelectedRequestId(requestId);
  };

  const closeRequestDetail = () => {
    setSelectedRequestId('');
    setSelectedRequest(null);
    setCallbackLogs([]);
    setRetargetFacilityId('');
    setRetargetEquipmentId('');
    setRetargetEquipmentOptions([]);
    setSimulateProcedureCode('');
    setSimulateEquipmentId('');
    setRequestActionMessage('');
    setClaimForm({ claim_id: '', payor: '', preauth_code: '' });
    setShaVerificationSource('hmis');
    setShaVerificationResult(null);
  };

  const loadRetargetEquipmentOptions = useCallback(async (facilityId) => {
    if (!facilityId || !selectedRequest?.id) {
      setRetargetEquipmentOptions([]);
      return;
    }
    try {
      const data = await requestService.getRetargetEligibleEquipment(selectedRequest.id, facilityId);
      setRetargetEquipmentOptions(data || []);
    } catch (_) {
      setRetargetEquipmentOptions([]);
    }
  }, [selectedRequest]);

  useEffect(() => {
    if (!selectedRequest) return;
    const initialFacilityId = selectedRequest.facility_id || '';
    const initialEquipmentId = (selectedRequest.equipment_assignments || [])[0]?.equipment_id || '';
    const initialProcedureCode = (selectedRequest.procedures || [])[0]?.procedure_code || '';
    setRetargetFacilityId(initialFacilityId);
    setRetargetEquipmentId(initialEquipmentId);
    setSimulateEquipmentId(initialEquipmentId);
    setSimulateProcedureCode(initialProcedureCode);
    setClaimForm({
      claim_id: selectedRequest.claim_id || '',
      payor: selectedRequest.payor || '',
      preauth_code: selectedRequest.preauth_code || '',
    });
    setShaVerificationResult(null);
    loadRetargetEquipmentOptions(initialFacilityId);
  }, [selectedRequest, loadRetargetEquipmentOptions]);

  const onRetargetFacilityChange = (facilityId) => {
    setRetargetFacilityId(facilityId);
    setRetargetEquipmentId('');
    loadRetargetEquipmentOptions(facilityId);
  };

  const handleRetargetRequest = async () => {
    if (!selectedRequest?.id) return;
    if (!retargetFacilityId || !retargetEquipmentId) {
      setError('Select both target facility and machine');
      return;
    }
    setError('');
    setRequestActionMessage('');
    setRetargetLoading(true);
    try {
      const result = await requestService.retarget(selectedRequest.id, {
        facility_id: retargetFacilityId,
        equipment_id: retargetEquipmentId,
      });
      setRequestActionMessage(`Retargeted to ${result.facility_id} / ${result.equipment_asset_id || result.equipment_dicom_aet || result.equipment_id}`);
      await Promise.all([
        loadRequestDetail(selectedRequest.id),
        loadRequests(page, true),
        loadMetrics(),
      ]);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to retarget request'));
    } finally {
      setRetargetLoading(false);
    }
  };

  const handleRegenerateMwl = async () => {
    if (!selectedRequest?.id) return;
    setError('');
    setRequestActionMessage('');
    setRegenerateLoading(true);
    try {
      const result = await requestService.regenerateMwl(selectedRequest.id);
      setRequestActionMessage(`Worklist resent: ${result.generated_files} worklist(s), ${result.succeeded} assignment(s) verified in Orthanc`);
      await Promise.all([
        loadRequestDetail(selectedRequest.id),
        loadRequests(page, true),
        loadMetrics(),
      ]);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to resend worklist'));
    } finally {
      setRegenerateLoading(false);
    }
  };

  const handleSimulateOrthancResult = async () => {
    if (!selectedRequest?.id) return;
    if (!simulateEquipmentId || !simulateProcedureCode) {
      setError('Select a machine and procedure before simulating a result');
      return;
    }

    setError('');
    setRequestActionMessage('');
    setSimulateResultLoading(true);
    try {
      const result = await requestService.simulateOrthancResult(selectedRequest.id, {
        equipment_id: simulateEquipmentId,
        procedure_code: simulateProcedureCode,
        status: 'final',
      });
      setRequestActionMessage(
        `${result.callback_message}: ${result.internal_request_id} (${toLabel(result.status)})`
      );
      await Promise.all([
        loadRequestDetail(selectedRequest.id),
        loadRequests(page, true),
        loadMetrics(),
      ]);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to simulate Orthanc result'));
    } finally {
      setSimulateResultLoading(false);
    }
  };

  const handleClaimUpdate = async () => {
    if (!selectedRequest?.internal_request_id) return;
    setError('');
    setRequestActionMessage('');
    setClaimUpdateLoading(true);
    try {
      const result = await requestService.updateClaim({
        internal_request_id: selectedRequest.internal_request_id,
        claim_id: claimForm.claim_id.trim() || null,
        payor: claimForm.payor.trim() || null,
        preauth_code: claimForm.preauth_code.trim() || null,
      });
      setSelectedRequest(result);
      setRequestActionMessage('Claim billing details updated.');
      await Promise.all([
        loadRequests(page, true),
        loadMetrics(),
      ]);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update claim details'));
    } finally {
      setClaimUpdateLoading(false);
    }
  };

  const handleShaVerification = async () => {
    const claimId = claimForm.claim_id.trim() || selectedRequest?.claim_id || '';
    if (!claimId) {
      setError('Enter a claim ID before running SHA verification');
      return;
    }

    setError('');
    setShaVerificationResult(null);
    setShaVerificationLoading(true);
    try {
      const result = await shaService.verifyClaimV2({
        claim_id: claimId,
        source: shaVerificationSource,
      });
      setShaVerificationResult(result);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to verify claim'));
    } finally {
      setShaVerificationLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1>Medical Requests</h1>
        {(process.env.REACT_APP_ENV === 'development') && (
          <button type="button" className={styles.addButton} onClick={() => setShowCreateModal(true)}>Add Request</button>
        )}
      </div>

      <div className={styles.analyticsGrid}>
        <div className={styles.metricCard}>
          <span>Total Requests</span>
          <strong>{metrics.total}</strong>
        </div>
        <div className={styles.metricCard}>
          <span>Completed</span>
          <strong>{metrics.completed}</strong>
        </div>
        <div className={styles.metricCard}>
          <span>Failed + Cancelled</span>
          <strong>{metrics.failed + metrics.cancelled}</strong>
        </div>
        <div className={styles.metricCard}>
          <span>Success Rate</span>
          <strong>{metrics.successRate.toFixed(1)}%</strong>
        </div>
        {claimSummary ? (
          <>
            <div className={styles.metricCard}>
              <span>With SHA Claim ID</span>
              <strong>{claimSummary.with_claim}</strong>
              <small>{sharePercent(claimSummary.with_claim, claimSummary.requests)} of {claimSummary.requests} filtered requests</small>
            </div>
            <div className={styles.metricCard}>
              <span>With Preauth</span>
              <strong>{claimSummary.with_preauth}</strong>
              <small>{sharePercent(claimSummary.with_preauth, claimSummary.requests)} of {claimSummary.requests} filtered requests</small>
            </div>
            <div className={styles.metricCard}>
              <span>With Claim ID and Preauth</span>
              <strong>{claimSummary.with_claim_and_preauth}</strong>
              <small>{sharePercent(claimSummary.with_claim_and_preauth, claimSummary.requests)} of {claimSummary.requests} filtered requests</small>
            </div>
            <div className={styles.metricCard}>
              <span>No Claim ID or Preauth</span>
              <strong>{claimSummary.requests - claimSummary.with_claim - claimSummary.with_preauth + claimSummary.with_claim_and_preauth}</strong>
              <small>Requests with neither</small>
            </div>
          </>
        ) : null}
      </div>

      {showCreateModal ? (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Create Medical Request (EMR Intake) - <strong className={styles.testing}>Testing Purposes only</strong></h2>
              <button type="button" className={styles.secondary} onClick={() => setShowCreateModal(false)}>Close</button>
            </div>
            {error ? <div className={styles.error}>{error}</div> : null}
            <form onSubmit={handleCreate} className={styles.grid4}>
          <input required placeholder="EMR Request ID" value={createForm.request_id} onChange={(e) => setCreateForm((prev) => ({ ...prev, request_id: e.target.value }))} />
          <input required placeholder="Patient ID / CR Number" value={createForm.patient_id} onChange={(e) => setCreateForm((prev) => ({ ...prev, patient_id: e.target.value }))} />
          <input required placeholder="Patient name" value={createForm.patient_first_name} onChange={(e) => setCreateForm((prev) => ({ ...prev, patient_first_name: e.target.value }))} />
          <input type="date" value={createForm.date_of_birth} onChange={(e) => setCreateForm((prev) => ({ ...prev, date_of_birth: e.target.value }))} />

          <select value={createForm.sex} onChange={(e) => setCreateForm((prev) => ({ ...prev, sex: e.target.value }))}>
            <option value="M">M</option>
            <option value="F">F</option>
            <option value="U">U</option>
            <option value="O">O</option>
          </select>
          <SearchableFacilitySelect
            facilities={facilities}
            value={createForm.facility_id}
            onChange={(facilityId) => setCreateForm((prev) => ({ ...prev, facility_id: facilityId }))}
            required
          />
          <select value={createForm.modality} onChange={(e) => setCreateForm((prev) => ({ ...prev, modality: e.target.value }))}>
            <option value="">Select modality</option>
            {modalities.map((modality) => (
              <option key={modality.id} value={modality.code}>{modality.code} - {modality.name}</option>
            ))}
          </select>
          <input placeholder="Institution name" value={createForm.institution_name} onChange={(e) => setCreateForm((prev) => ({ ...prev, institution_name: e.target.value }))} />

          <input type="url" placeholder="Callback URL (optional; falls back to facility)" value={createForm.callback_url} onChange={(e) => setCreateForm((prev) => ({ ...prev, callback_url: e.target.value }))} />
          <select value={createForm.callback_auth_type} onChange={(e) => setCreateForm((prev) => ({ ...prev, callback_auth_type: e.target.value }))}>
            <option value="none">No authentication</option>
            <option value="Bearer">Bearer token</option>
            <option value="Basic">Basic token</option>
          </select>

          <input className={styles.colSpan3} required placeholder="Idempotency key" value={createForm.idempotency_key} onChange={(e) => setCreateForm((prev) => ({ ...prev, idempotency_key: e.target.value }))} />
          <button type="button" className={styles.secondary} onClick={generateIdempotencyKey}>Generate key</button>

          <input placeholder="Claim ID (optional)" value={createForm.claim_id} onChange={(e) => setCreateForm((prev) => ({ ...prev, claim_id: e.target.value }))} />
          <input placeholder="Payor (optional)" value={createForm.payor} onChange={(e) => setCreateForm((prev) => ({ ...prev, payor: e.target.value }))} />
          <input className={styles.colSpan2} placeholder="Preauth code (optional)" value={createForm.preauth_code} onChange={(e) => setCreateForm((prev) => ({ ...prev, preauth_code: e.target.value }))} />

          <input className={styles.colSpan4} placeholder="Callback auth token (optional)" value={createForm.callback_auth_token} onChange={(e) => setCreateForm((prev) => ({ ...prev, callback_auth_token: e.target.value }))} />

          <textarea
            className={styles.colSpan4}
            placeholder="Description"
            value={createForm.description}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, description: e.target.value }))}
          />

          <div className={styles.colSpan4}>
            <label className={styles.groupTitle}>Procedure Codes</label>
            <div className={styles.procedureGrid}>
              {procedures.map((procedure) => (
                <label key={procedure.id} className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    checked={createForm.procedures.includes(procedure.procedure_code)}
                    onChange={() => handleProcedureSelection(procedure.procedure_code)}
                  />
                  <span>{procedure.procedure_code} - {procedure.name}</span>
                </label>
              ))}
              {procedures.length === 0 ? <span>No procedures available</span> : null}
            </div>
          </div>

          <textarea
            className={styles.colSpan4}
            placeholder="Request metadata JSON (optional)"
            value={createForm.request_metadata}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, request_metadata: e.target.value }))}
          />
              <div className={styles.modalActions}>
                <button type="submit" disabled={createLoading}>{createLoading ? 'Submitting...' : 'Create Request'}</button>
                <button type="button" className={styles.secondary} onClick={() => setShowCreateModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <div className={styles.panel}>
        <h2>Search & Filter</h2>
        <form onSubmit={onApplyFilters} className={styles.grid4}>
          <input placeholder="Search current page (EMR ID, patient, claim, payor, facility, status)" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {requestStatuses.map((status) => (
              <option key={status} value={status}>{toLabel(status)}</option>
            ))}
          </select>
          <input placeholder="Filter by patient" value={patientFilter} onChange={(e) => setPatientFilter(e.target.value)} />
          <input placeholder="Filter by facility name" value={facilityFilter} onChange={(e) => setFacilityFilter(e.target.value)} />
          <select value={claimFilter} onChange={(e) => { setClaimFilter(e.target.value); setPage(1); }} aria-label="SHA claim ID">
            <option value="">Any claim ID</option>
            <option value="true">Has SHA claim ID</option>
            <option value="false">No SHA claim ID</option>
          </select>
          <select value={preauthFilter} onChange={(e) => { setPreauthFilter(e.target.value); setPage(1); }} aria-label="Preauth">
            <option value="">Any preauth</option>
            <option value="true">Has preauth</option>
            <option value="false">No preauth</option>
          </select>
          {vendors.length ? (
            <select value={vendorFilter} onChange={(e) => { setVendorFilter(e.target.value); setPage(1); }} aria-label="Vendor">
              <option value="">All vendors</option>
              {vendors.map((vendor) => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}
            </select>
          ) : null}
          <button type="submit">Apply</button>
          <button type="button" className={styles.secondary} onClick={exportRequests} disabled={exporting}>
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </form>
      </div>

      {!showCreateModal && !selectedRequestId && error ? <div className={styles.error}>{error}</div> : null}
      {createMessage ? <div className={styles.success}>{createMessage}</div> : null}

      <WorklistRepublishPanel canWrite={canWriteRequests} onRepublished={() => loadRequests()} />

      <div className={styles.panel}>
        <div className={styles.headerRow}>
          <h2>Requests List</h2>
          <label className={styles.refreshToggle}>
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            Auto refresh every 10s
          </label>
        </div>
        {loading ? <p>Loading requests...</p> : null}
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th className={styles.sortable} onClick={() => changeSort('internal_request_id')}>Internal ID</th>
                <th className={styles.sortable} onClick={() => changeSort('emr_request_id')}>EMR ID</th>
                <th className={styles.sortable} onClick={() => changeSort('patient_first_name')}>Patient</th>
                <th className={styles.sortable} onClick={() => changeSort('payor')}>Payor</th>
                <th className={styles.sortable} onClick={() => changeSort('facility')}>Facility</th>
                <th className={styles.sortable} onClick={() => changeSort('status')}>Status</th>
                <th>Worklist Pulled</th>
                <th className={styles.sortable} onClick={() => changeSort('created_at')}>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item, index) => (
                <tr key={item.id}>
                  <td>{(page - 1) * pageSize + index + 1}</td>
                  <td>{item.internal_request_id}</td>
                  <td>{item.emr_request_id}</td>
                  <td>{item.patient_id} ({item.patient_first_name})</td>
                  <td>
                    <div className={styles.stack}>
                      <strong>{item.payor || '-'}</strong>
                      <span className={styles.muted}>{item.preauth_code || item.claim_id || '-'}</span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.stack}>
                      <strong>{getFacilityName(item.facility_id)}</strong>
                      <span className={styles.muted}>{item.facility_id}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`${styles.status} ${styles[`status_${item.status}`]}`}>{toLabel(item.status)}</span>
                  </td>
                  <td>
                    {item.worklist_last_pulled_at
                      ? toDateTime(item.worklist_last_pulled_at)
                      : <span className={styles.muted}>Not yet</span>}
                  </td>
                  <td>{toDateTime(item.created_at)}</td>
                  <td>
                    <button type="button" onClick={() => openRequestDetail(item.id)}>View Detail</button>
                  </td>
                </tr>
              ))}
              {!loading && sortedItems.length === 0 ? (
                <tr>
                  <td colSpan={10}>No requests found.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className={styles.pagination}>
          <button disabled={page <= 1} onClick={() => setPage((prev) => prev - 1)}>Previous</button>
          <span>Page {page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((prev) => prev + 1)}>Next</button>
        </div>
      </div>

      {selectedRequestId ? (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <h2>Request Detail & Timeline</h2>
            <button type="button" className={styles.secondary} onClick={closeRequestDetail}>Close</button>
          </div>
          {error ? <div className={styles.error}>{error}</div> : null}
          {requestActionMessage ? <div className={styles.success}>{requestActionMessage}</div> : null}
          {detailLoading ? <p>Loading request detail...</p> : null}

          {selectedRequest ? (
            <div className={styles.detailGrid}>
              <div><strong>Internal ID:</strong> {selectedRequest.internal_request_id}</div>
              <div><strong>EMR ID:</strong> {selectedRequest.emr_request_id}</div>
              <div><strong>Status:</strong> {toLabel(selectedRequest.status)}</div>
              <div><strong>Patient:</strong> {selectedRequest.patient_id} ({selectedRequest.patient_first_name})</div>
              <div><strong>Claim ID:</strong> {selectedRequest.claim_id || '-'}</div>
              <div><strong>Payor:</strong> {selectedRequest.payor || '-'}</div>
              <div><strong>Preauth Code:</strong> {selectedRequest.preauth_code || '-'}</div>
              <div><strong>DOB:</strong> {selectedRequest.request_metadata?.date_of_birth || '-'}</div>
              <div><strong>Sex:</strong> {selectedRequest.request_metadata?.sex || '-'}</div>
              <div><strong>Modality:</strong> {selectedRequest.request_metadata?.modality || '-'}</div>
              <div><strong>Facility:</strong> {selectedRequest.facility_id}</div>
              <div><strong>Institution:</strong> {selectedRequest.request_metadata?.institution_name || '-'}</div>
              <div><strong>Machine:</strong> {currentAssignment?.equipment_id || '-'}</div>
              <div><strong>Callback URL:</strong> {selectedRequest.callback_url}</div>
              <div className={styles.colSpan2}><strong>Description:</strong> {selectedRequest.request_metadata?.description || '-'}</div>
              <div className={styles.colSpan2}><strong>Status Message:</strong> {selectedRequest.status_message || '-'}</div>
            </div>
          ) : null}

          {selectedRequest ? (
            <>
              <h3>Requested Procedures</h3>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Code</th>
                      <th>Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedRequest.procedures || []).map((procedure) => (
                      <tr key={procedure.id}>
                        <td>{procedure.sequence_number}</td>
                        <td>{procedure.procedure_code}</td>
                        <td>{procedure.procedure_name}</td>
                      </tr>
                    ))}
                    {(selectedRequest.procedures || []).length === 0 ? (
                      <tr>
                        <td colSpan={3}>No procedures found on this request.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>

              <h3>Received Studies</h3>
              <ReceivedStudiesTable
                items={selectedRequest.received_studies || []}
                compact
                emptyText="No images received for this request yet."
              />

              <h3>Worklist Pulls</h3>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Machine AE</th>
                      <th>Published</th>
                      <th>First Pulled</th>
                      <th>Last Pulled</th>
                      <th>Times</th>
                      <th>From IP</th>
                      <th>Worklist</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedRequest.worklist_pulls || []).map((pull) => (
                      <tr key={pull.orthanc_worklist_id}>
                        <td>{pull.scheduled_station_ae_title}</td>
                        <td>{toDateTime(pull.created_at)}</td>
                        <td>{pull.first_pulled_at ? toDateTime(pull.first_pulled_at) : <span className={styles.muted}>Not pulled yet</span>}</td>
                        <td>{toDateTime(pull.last_pulled_at)}</td>
                        <td>{pull.pull_count}</td>
                        <td>{pull.last_pulled_ip || '-'}</td>
                        <td>{toLabel(pull.status)}</td>
                      </tr>
                    ))}
                    {(selectedRequest.worklist_pulls || []).length === 0 ? (
                      <tr>
                        <td colSpan={7}>No worklist has been published for this request.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}

          {selectedRequest ? (
            <div className={styles.detailActionsGrid}>
              <div className={styles.actionCard}>
                <h3>Claim Billing</h3>
                <div className={styles.grid}>
                  <input
                    placeholder="Claim ID"
                    value={claimForm.claim_id}
                    onChange={(e) => setClaimForm((prev) => ({ ...prev, claim_id: e.target.value }))}
                  />
                  <br />
                  <input
                    placeholder="Payor"
                    value={claimForm.payor}
                    onChange={(e) => setClaimForm((prev) => ({ ...prev, payor: e.target.value }))}
                  />
                  <br />
                  <input
                    placeholder="Preauth code"
                    value={claimForm.preauth_code}
                    onChange={(e) => setClaimForm((prev) => ({ ...prev, preauth_code: e.target.value }))}
                  />
                </div>
                <button type="button" onClick={handleClaimUpdate} disabled={claimUpdateLoading}>
                  {claimUpdateLoading ? 'Saving...' : 'Save Claim Details'}
                </button>
                <div className={styles.grid2}>
                  <select value={shaVerificationSource} onChange={(e) => setShaVerificationSource(e.target.value)}>
                    <option value="hmis">HMIS</option>
                    <option value="provider_portal">Provider Portal</option>
                  </select>
                  <button type="button" onClick={handleShaVerification} disabled={shaVerificationLoading || !claimForm.claim_id.trim()}>
                    {shaVerificationLoading ? 'Verifying...' : 'Verify Claim'}
                  </button>
                </div>
                {shaVerificationResult ? (
                  <pre className={styles.resultBox}>{JSON.stringify(shaVerificationResult, null, 2)}</pre>
                ) : null}
              </div>
              <div className={styles.actionCard}>
                <h3>Resend Worklist</h3>
                <p>Use this when the worklist does not exist in Orthanc or needs to be published again.</p>
                {!hasMwlGenerated ? <span className={styles.warningText}>No Orthanc worklist found for this request.</span> : null}
                <button
                  type="button"
                  onClick={handleRegenerateMwl}
                  disabled={regenerateLoading || selectedRequest.status === 'completed' || selectedRequest.status === 'cancelled'}
                >
                  {regenerateLoading ? 'Resending...' : 'Resend Worklist'}
                </button>
              </div>
              <div className={styles.actionCard}>
                <h3>Change Target Facility / Machine</h3>
                <p>Retarget assignment, then resend the worklist so the selected machine can pull the updated order.</p>
                <div className={styles.grid2}>
                  <SearchableFacilitySelect
                    facilities={facilities}
                    value={retargetFacilityId}
                    onChange={onRetargetFacilityChange}
                  />
                  <br />
                  <select value={retargetEquipmentId} onChange={(e) => setRetargetEquipmentId(e.target.value)}>
                    <option value="">Select machine</option>
                    {retargetEquipmentOptions.map((equipment) => (
                      <option key={equipment.id} value={equipment.id}>
                        {equipment.asset_id} - {equipment.dicom_aet || '-'} ({equipment.category || '-'})
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={handleRetargetRequest}
                  disabled={retargetLoading || !retargetFacilityId || !retargetEquipmentId || selectedRequest.status === 'completed' || selectedRequest.status === 'cancelled'}
                >
                  {retargetLoading ? 'Updating target...' : 'Update Target'}
                </button>
              </div>

              <div className={styles.actionCard}>
                <h3>Simulate Orthanc Result</h3>
                <p>Record a test result as if Orthanc sent it, then send it to the request callback or facility EMR callback.</p>
                <div className={styles.grid}>
                  <select value={simulateEquipmentId} onChange={(e) => setSimulateEquipmentId(e.target.value)}>
                    <option value="">Select machine</option>
                    {(selectedRequest.equipment_assignments || []).map((assignment) => (
                      <option key={assignment.id} value={assignment.equipment_id}>
                        {assignment.equipment_id}
                      </option>
                    ))}
                  </select>
                  <br />
                  
                  <select value={simulateProcedureCode} onChange={(e) => setSimulateProcedureCode(e.target.value)}>
                    <option value="">Select procedure</option>
                    {(selectedRequest.procedures || []).map((procedure) => (
                      <option key={procedure.id} value={procedure.procedure_code}>
                        {procedure.procedure_code} - {procedure.procedure_name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={handleSimulateOrthancResult}
                  disabled={
                    simulateResultLoading ||
                    !simulateEquipmentId ||
                    !simulateProcedureCode ||
                    selectedRequest.status === 'cancelled'
                  }
                >
                  {simulateResultLoading ? 'Sending result...' : 'Simulate Result & Callback'}
                </button>
              </div>
            </div>
          ) : null}

          <h3>Timeline</h3>
          <ul className={styles.timeline}>
            {timeline.map((event) => (
              <li key={event.key}>
                <div className={styles.timelineTime}>{toDateTime(event.at)}</div>
                <div>
                  <strong>{event.title}</strong>
                  <p>{event.description}</p>
                </div>
              </li>
            ))}
            {timeline.length === 0 ? <li>No timeline events yet.</li> : null}
          </ul>

          <h3>Callback Logs</h3>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Attempt</th>
                  <th>Status</th>
                  <th>HTTP</th>
                  <th>Created</th>
                  <th>Completed</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {callbackLogs.map((log) => (
                  <tr key={log.id}>
                    <td>{log.attempt_number}</td>
                    <td>{toLabel(log.callback_status)}</td>
                    <td>{log.http_status_code || '-'}</td>
                    <td>{toDateTime(log.created_at)}</td>
                    <td>{toDateTime(log.completed_at)}</td>
                    <td>{log.error_message || '-'}</td>
                  </tr>
                ))}
                {callbackLogs.length === 0 ? (
                  <tr>
                    <td colSpan={6}>No callback logs for this request.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <div className={styles.modalActions}>
            <button type="button" className={styles.secondary} onClick={closeRequestDetail}>Close</button>
          </div>
        </div>
        </div>
      ) : null}
    </div>
  );
};

export default RequestsPage;
